import { useState } from "react";
import { ArrowUp, ArrowDown, Wallet } from "lucide-react";
import clsx from "clsx";
import { Button } from "@/components/ui/Button";
import { LivePnL } from "./LivePnL";
import { PositionSizeCalc } from "./PositionSizeCalc";
import { StatsPanel } from "./StatsPanel";
import { DrawingEditPanel } from "@/components/chart/DrawingEditPanel";
import { useTradingStore } from "@/store/trading";
import { useDrawingsStore } from "@/store/drawings";
import { formatPrice } from "@/lib/format";
import type { TradeRow } from "@/hooks/useTrades";

interface TradingPanelProps {
  currentPrice: number | null;
  /** True when SL/TP auto-close is actually running (single-pane replay). */
  autoCloseActive?: boolean;
  onBuy: () => void;
  onSell: () => void;
  onClosePosition: () => void;
  onUpdateSL: (price: number | null) => void;
  onUpdateTP: (price: number | null) => void;
  trades: TradeRow[];
}

export function TradingPanel({
  currentPrice,
  autoCloseActive,
  onBuy,
  onSell,
  onClosePosition,
  onUpdateSL,
  onUpdateTP,
  trades,
}: TradingPanelProps) {
  const {
    accountBalance,
    riskPercent,
    defaultQuantity,
    open,
    setAccountBalance,
    setRiskPercent,
    setDefaultQuantity,
  } = useTradingStore();

  const [qty, setQty] = useState<number>(defaultQuantity);

  // Update default qty in store when user changes it (so it persists)
  const handleQtyChange = (v: number) => {
    setQty(v);
    if (v > 0) setDefaultQuantity(v);
  };

  return (
    <div className="flex h-full flex-col gap-3 p-3">
      {/* Account */}
      <section className="rounded-md border border-border bg-bg-elevated p-2.5">
        <div className="mb-2 flex items-center gap-1.5 text-2xs uppercase tracking-wider text-text-muted">
          <Wallet className="h-3 w-3" />
          <span>Account</span>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <label className="flex flex-col gap-1">
            <span className="text-2xs text-text-secondary">Balance</span>
            <input
              type="number"
              value={accountBalance}
              onChange={(e) => setAccountBalance(Number(e.target.value))}
              className="rounded-md border border-border bg-bg-deep px-2 py-1 text-xs font-mono tabular text-text-primary focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
            />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-2xs text-text-secondary">Risk %</span>
            <input
              type="number"
              step="0.1"
              min="0.1"
              max="100"
              value={riskPercent}
              onChange={(e) => setRiskPercent(Number(e.target.value))}
              className="rounded-md border border-border bg-bg-deep px-2 py-1 text-xs font-mono tabular text-text-primary focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
            />
          </label>
        </div>
      </section>

      {/* Quantity + Buy/Sell */}
      <section className="rounded-md border border-border bg-bg-elevated p-2.5">
        <label className="mb-2 flex flex-col gap-1">
          <span className="text-2xs uppercase tracking-wider text-text-muted">Quantity</span>
          <input
            type="number"
            min="1"
            value={qty}
            onChange={(e) => handleQtyChange(Number(e.target.value))}
            className="rounded-md border border-border bg-bg-deep px-2 py-1.5 text-sm font-mono tabular text-text-primary focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
          />
        </label>

        <div className="grid grid-cols-2 gap-2">
          <Button
            onClick={onBuy}
            disabled={!!open || !currentPrice || qty < 1}
            className={clsx(
              "bg-bull text-white hover:bg-bull/85 disabled:bg-bull/30",
              "flex items-center justify-center gap-1.5"
            )}
            title="Buy at current candle close (B)"
          >
            <ArrowUp className="h-4 w-4" />
            <span>Buy</span>
            <kbd className="rounded bg-black/20 px-1 py-px text-[10px] text-white/80">B</kbd>
          </Button>
          <Button
            onClick={onSell}
            disabled={!!open || !currentPrice || qty < 1}
            className={clsx(
              "bg-bear text-white hover:bg-bear/85 disabled:bg-bear/30",
              "flex items-center justify-center gap-1.5"
            )}
            title="Sell at current candle close (S)"
          >
            <ArrowDown className="h-4 w-4" />
            <span>Sell</span>
            <kbd className="rounded bg-black/20 px-1 py-px text-[10px] text-white/80">S</kbd>
          </Button>
        </div>

        {currentPrice !== null && (
          <div className="mt-2 text-2xs text-text-muted">
            Entry @ <span className="font-mono text-text-primary">{formatPrice(currentPrice)}</span>
          </div>
        )}

        {open && (
          <div className="mt-2 rounded bg-warn/15 px-2 py-1 text-2xs text-warn">
            Close current position before opening a new one.
          </div>
        )}
      </section>

      {/* Position sizer */}
      {!open && (
        <PositionSizeCalc
          accountBalance={accountBalance}
          riskPercent={riskPercent}
          currentPrice={currentPrice}
          onApply={handleQtyChange}
        />
      )}

      {/* Open position */}
      {open && (
        <LivePnL
          open={open}
          autoCloseActive={autoCloseActive}
          currentPrice={currentPrice}
          onUpdateSL={onUpdateSL}
          onUpdateTP={onUpdateTP}
          onClose={onClosePosition}
        />
      )}

      {/* Selected drawing edit panel */}
      <DrawingSelectedEdit />

      {/* Stats */}
      <StatsPanel trades={trades} accountBalance={accountBalance} />
    </div>
  );
}

/** Inline subcomponent that subscribes to the drawings store and renders the edit panel only when something is selected. */
function DrawingSelectedEdit() {
  const selectedId = useDrawingsStore((s) => s.selectedId);
  if (!selectedId) return null;
  return <DrawingEditPanel drawingId={selectedId} />;
}
